import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { onSignOut } from '../lib/signOutReset';
import { useCurrentView } from '../hooks/useCurrentView';
import type { View } from '../lib/viewRouter';
import type { Guild } from '../types';

// Guild "mode" — entering a guild keeps the app shell but swaps the nav rail
// for the guild's own navigation (plan §0.3). Deliberately NOT persisted:
// a reload lands back on the /guilds browse page.

export type GuildModuleId =
  | 'feed'
  | 'members'
  | 'rooms'
  | 'pages'
  | 'quests'
  | 'events'
  | 'library';

export type GuildRole = 'leader' | 'admin' | 'mentor' | 'member' | 'apprentice' | null;

interface GuildModeState {
  /** The guild we're inside — null = not in guild mode. */
  activeGuildId: string | null;
  activeModule: GuildModuleId;
  /** Loaded snapshot of the active guild (rail + workspace both read it). */
  guild: Guild | null;
  isMember: boolean;
  userRole: GuildRole;
  /** My auth user id (set on load) — owner checks (guild.created_by). */
  myId: string | null;
  isLoading: boolean;
  showSettings: boolean;
  /** Browse-page selection — survives entering/exiting a guild. */
  browseSelectedId: string | null;
  /**
   * The view we came from, so leaving a guild puts us back there.
   *
   * ⚠️ Guilds is reachable from TWO places — its own view and the Discover
   * ribbon's Guilds tab — and without this, entering from Discover and coming
   * back would silently drop you on the standalone page with the ribbon gone.
   */
  returnView: View | null;

  enterGuild: (guildId: string) => void;
  /**
   * Leave guild mode WITHOUT navigating.
   *
   * ⚠️⚠️ MUST NOT NAVIGATE. `App.tsx` calls this from an effect whenever the
   * view moves off `guilds`, so a navigation here would fight the user: they
   * click Home, the effect fires, and we yank them back to `returnView`. The
   * explicit back buttons call `exitGuildToReturn` instead.
   */
  exitGuild: () => void;
  /** Leave guild mode AND go back to wherever the guild was opened from. */
  exitGuildToReturn: () => void;
  setModule: (module: GuildModuleId) => void;
  setShowSettings: (show: boolean) => void;
  setBrowseSelectedId: (guildId: string | null) => void;
  /** (Re)fetch the active guild row + my membership. */
  loadActiveGuild: () => Promise<void>;
  joinActiveGuild: () => Promise<boolean>;
  leaveActiveGuild: () => Promise<boolean>;
  /** Forget who I am in the guild — run on sign-out (lib/signOutReset). */
  clearStore: () => void;
}

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

export const useGuildModeStore = create<GuildModeState>()((set, get) => ({
  activeGuildId: null,
  activeModule: 'feed',
  guild: null,
  isMember: false,
  userRole: null,
  myId: null,
  isLoading: false,
  showSettings: false,
  browseSelectedId: null,
  returnView: null,

  enterGuild: (guildId) => {
    /**
     * ⚠️⚠️ ENTERING A GUILD MUST ALSO NAVIGATE TO THE `guilds` VIEW.
     *
     * Guild mode swaps the nav rail but the CANVAS is chosen by `currentView`,
     * and this used to set only the mode. Opened from the standalone `guilds`
     * view that looked fine — the view already matched. Opened from the
     * DISCOVER ribbon's Guilds tab it left the app half-switched: the rail
     * became the guild's while the canvas kept rendering the browse page,
     * because `App.tsx` still saw `currentView === 'social'`.
     *
     * ⚠️ It also silently defeated App's safety effect. That effect exits guild
     * mode whenever the view is not `guilds`, but it is keyed on `[currentView]`
     * — which never changed here — so nothing ever re-evaluated and the mode
     * stayed active on a foreign page. Navigating is what makes the two halves
     * agree, and it is why the fix belongs here rather than at a button.
     */
    const view = useCurrentView.getState();
    set({
      activeGuildId: guildId,
      activeModule: 'feed',
      guild: null,
      isMember: false,
      userRole: null,
      showSettings: false,
      browseSelectedId: guildId,
      // Where to return on the way out. Already on `guilds` = stay there.
      returnView: view.currentView,
    });
    if (view.currentView !== 'guilds') view.setCurrentView('guilds');
    get().loadActiveGuild();
  },

  exitGuild: () =>
    set({
      activeGuildId: null,
      guild: null,
      isMember: false,
      userRole: null,
      showSettings: false,
      activeModule: 'feed',
    }),

  exitGuildToReturn: () => {
    // ⚠️ Read BEFORE clearing — `exitGuild` does not reset `returnView`, but
    // reading first keeps this correct if it ever does.
    const back = get().returnView;
    get().exitGuild();
    const view = useCurrentView.getState();
    // Default to the standalone browse page: a guild opened by a deep link has
    // no recorded origin, and stranding the user in a guild-less guild view
    // would be worse than landing them on the list.
    const target: View = back ?? 'guilds';
    if (view.currentView !== target) view.setCurrentView(target);
  },

  setModule: (module) => set({ activeModule: module }),
  setShowSettings: (show) => set({ showSettings: show }),
  setBrowseSelectedId: (guildId) => set({ browseSelectedId: guildId }),

  loadActiveGuild: async () => {
    const guildId = get().activeGuildId;
    if (!guildId) return;
    const at = epoch;
    set({ isLoading: true });
    try {
      const [{ data: guild }, { user }] = await Promise.all([
        supabase.from('guilds').select('*').eq('id', guildId).single(),
        syncManager.safeAuthCheck(),
      ]);
      // mode may have been exited (or switched) while fetching
      if (get().activeGuildId !== guildId) return;

      let isMember = false;
      let userRole: GuildRole = null;
      if (user) {
        const { data: membership } = await supabase
          .from('guild_members')
          .select('member_role')
          .eq('guild_id', guildId)
          .eq('user_id', user.id)
          .maybeSingle();
        if (membership) {
          isMember = true;
          userRole = membership.member_role as GuildRole;
        }
      }
      // Exited or switched while fetching — or signed out, and the membership and id
      // are the account's that left.
      if (at !== epoch || get().activeGuildId !== guildId) return;
      set({ guild: guild ?? null, isMember, userRole, myId: user?.id ?? null });
    } catch (error) {
      console.error('Error loading active guild:', error);
    } finally {
      if (at === epoch && get().activeGuildId === guildId) set({ isLoading: false });
    }
  },

  joinActiveGuild: async () => {
    const guildId = get().activeGuildId;
    if (!guildId) return false;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return false;
      const { error } = await supabase
        .from('guild_members')
        .insert({ guild_id: guildId, user_id: user.id, member_role: 'member' });
      if (error) throw error;
      await get().loadActiveGuild();
      return true;
    } catch (error) {
      console.error('Error joining guild:', error);
      return false;
    }
  },

  leaveActiveGuild: async () => {
    const guildId = get().activeGuildId;
    if (!guildId) return false;
    try {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return false;
      const { error } = await supabase
        .from('guild_members')
        .delete()
        .eq('guild_id', guildId)
        .eq('user_id', user.id);
      if (error) throw error;
      await get().loadActiveGuild();
      return true;
    } catch (error) {
      console.error('Error leaving guild:', error);
      return false;
    }
  },

  /**
   * ⚠️ The PER-USER fields only (my id, my role, my membership): the guild open on screen
   * is the same for anyone, and leaving guild mode is App's to do (a sign-out lands on
   * Home, which exits it). A load still in flight for the account that left is dropped.
   */
  clearStore: () => {
    epoch += 1;
    set({ myId: null, userRole: null, isMember: false, isLoading: false });
  },
}));

onSignOut(() => useGuildModeStore.getState().clearStore());
